import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import "./SkeletonLoader.css";

/**
 * SkeletonLoader - Componentes de skeleton loading para mejor UX
 * 
 * Muestra placeholders animados mientras se cargan los datos,
 * dando feedback visual al usuario de que algo está cargando.
 */

/**
 * Skeleton básico - Línea animada
 */
export const Skeleton = ({ width = "100%", height = "20px", className = "" }) => (
  <div
    className={`skeleton ${className}`}
    style={{
      width,
      height,
      backgroundColor: "#e0e0e0",
      borderRadius: "4px",
      animation: "skeleton-loading 1.5s infinite ease-in-out",
    }}
  />
);

/**
 * SkeletonText - Múltiples líneas de texto
 */
export const SkeletonText = ({ lines = 3, gap = "12px" }) => (
  <div style={{ display: "flex", flexDirection: "column", gap }}>
    {[...Array(lines)].map((_, index) => (
      <Skeleton
        key={index}
        width={index === lines - 1 ? "70%" : "100%"}
        height="16px"
      />
    ))}
  </div>
);

/**
 * SkeletonCard - Card con skeleton
 */
export const SkeletonCard = ({ hasHeader = true, lines = 4 }) => (
  <Card>
    {hasHeader && (
      <CardHeader>
        <Skeleton width="40%" height="24px" />
      </CardHeader>
    )}
    <CardBody>
      <SkeletonText lines={lines} />
    </CardBody>
  </Card>
);

/**
 * SkeletonTable - Tabla con skeleton
 */
export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div className="table-responsive">
    <table className="table">
      <thead>
        <tr>
          {[...Array(columns)].map((_, index) => (
            <th key={index}>
              <Skeleton height="20px" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...Array(rows)].map((_, rowIndex) => (
          <tr key={rowIndex}>
            {[...Array(columns)].map((_, colIndex) => (
              <td key={colIndex}>
                <Skeleton height="16px" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * SkeletonAvatar - Avatar circular
 */
export const SkeletonAvatar = ({ size = "48px" }) => (
  <div
    className="skeleton"
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: "#e0e0e0",
      animation: "skeleton-loading 1.5s infinite ease-in-out",
    }}
  />
);

/**
 * SkeletonSolicitud - Skeleton específico para detalle de solicitud
 */
export const SkeletonSolicitud = () => (
  <div>
    {/* Header */}
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex align-items-center gap-3 mb-3">
          <SkeletonAvatar size="60px" />
          <div className="flex-grow-1">
            <Skeleton width="200px" height="24px" className="mb-2" />
            <Skeleton width="150px" height="16px" />
          </div>
        </div>
        <div className="d-flex gap-2">
          <Skeleton width="100px" height="32px" />
          <Skeleton width="100px" height="32px" />
          <Skeleton width="100px" height="32px" />
        </div>
      </CardBody>
    </Card>

    <div className="row">
      {/* Columna principal */}
      <div className="col-xxl-9">
        <SkeletonCard lines={6} />
        <SkeletonCard lines={4} className="mt-3" />
        <SkeletonCard lines={5} className="mt-3" />
      </div>

      {/* Sidebar */}
      <div className="col-xxl-3">
        <SkeletonCard hasHeader={false} lines={3} />
        <SkeletonCard hasHeader={false} lines={4} className="mt-3" />
      </div>
    </div>
  </div>
);

/**
 * SkeletonList - Lista de items
 */
export const SkeletonList = ({ items = 5, hasAvatar = false }) => (
  <div>
    {[...Array(items)].map((_, index) => (
      <div
        key={index}
        className="d-flex align-items-center gap-3 mb-3 p-3 border rounded"
      >
        {hasAvatar && <SkeletonAvatar size="40px" />}
        <div className="flex-grow-1">
          <Skeleton width="60%" height="18px" className="mb-2" />
          <Skeleton width="40%" height="14px" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * SkeletonForm - Formulario con skeleton
 */
export const SkeletonForm = ({ fields = 4 }) => (
  <div>
    {[...Array(fields)].map((_, index) => (
      <div key={index} className="mb-3">
        <Skeleton width="120px" height="16px" className="mb-2" />
        <Skeleton width="100%" height="38px" />
      </div>
    ))}
    <div className="d-flex gap-2 mt-4">
      <Skeleton width="100px" height="38px" />
      <Skeleton width="100px" height="38px" />
    </div>
  </div>
);

/**
 * SkeletonWidget - Widget de estadísticas
 */
export const SkeletonWidget = () => (
  <Card className="card-height-100">
    <CardBody>
      <div className="d-flex align-items-center mb-3">
        <div className="flex-grow-1">
          <Skeleton width="60%" height="16px" className="mb-2" />
          <Skeleton width="40%" height="28px" />
        </div>
        <SkeletonAvatar size="48px" />
      </div>
      <Skeleton width="100%" height="80px" />
    </CardBody>
  </Card>
);

/**
 * SkeletonDashboard - Dashboard completo
 */
export const SkeletonDashboard = () => (
  <div>
    {/* KPIs */}
    <div className="row mb-4">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="col-xl-3 col-md-6">
          <SkeletonWidget />
        </div>
      ))}
    </div>

    {/* Gráficos */}
    <div className="row">
      <div className="col-xl-8">
        <SkeletonCard lines={8} />
      </div>
      <div className="col-xl-4">
        <SkeletonCard lines={6} />
      </div>
    </div>
  </div>
);

export default Skeleton;
